import i18next from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Container, Row, Table } from 'react-bootstrap';
import { FaCheck, FaEnvelope, FaRegCopy } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import toaster from 'src/utils/toaster';

type Contact = {
  label: string;
  email: string;
};

const COPIED_FEEDBACK_MS = 1000;

const CONTACTS: Contact[] = [
  {
    label: i18next.t('contacts_lite_lease_activation', {
      defaultValue: 'Prenotazione/Attivazione Lite Lease',
    }),
    email: 'attivazionilitelease@horizonautomotive.com',
  },
  {
    label: i18next.t('contacts_lite_lease_support', {
      defaultValue: 'Supporto vetture Lite Lease',
    }),
    email: 'liteleasesupport@horizonautomotive.it',
  },
  {
    label: i18next.t('contacts_short_term', { defaultValue: 'Short Term' }),
    email: 'short-term@horizonautomotive.com',
  },
  {
    label: i18next.t('contacts_product_team', { defaultValue: 'Team Product' }),
    email: 'product@horizonautomotive.com',
  },
  {
    label: i18next.t('contacts_billing', {
      defaultValue: 'Fatturazione/Amministrazione',
    }),
    email: 'reporting_provvigioni@horizonautomotive.com',
  },
];

const CopyEmailButton = ({ email }: { email: string }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const resetTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(resetTimeout.current), []);

  const label = copied
    ? t('contacts_email_copied', { defaultValue: 'Email copiata' })
    : t('contacts_copy_email', { defaultValue: 'Copia email' });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      clearTimeout(resetTimeout.current);
      resetTimeout.current = setTimeout(
        () => setCopied(false),
        COPIED_FEEDBACK_MS
      );
    } catch {
      toaster.error(
        t('contacts_copy_failed', { defaultValue: 'Copia non riuscita' })
      );
    }
  };

  return (
    <Button
      variant="link"
      className={`p-0 lh-1 ${copied ? 'text-success' : 'text-muted'}`}
      aria-label={label}
      title={label}
      onClick={handleCopy}
    >
      {copied ? <FaCheck /> : <FaRegCopy />}
    </Button>
  );
};

const ContactsPage = () => {
  const { t } = useTranslation();

  return (
    <Container fluid>
      <Row>
        <Col className="display-6">
          {t('contacts_and_support', { defaultValue: 'Contatti e supporto' })}
        </Col>
      </Row>
      <Row className="mt-2">
        <Col>
          <p className="text-muted">
            {t('contacts_and_support_intro', {
              defaultValue: 'Scrivi al team giusto per ricevere supporto.',
            })}
          </p>
        </Col>
      </Row>
      <Card>
        <Card.Body>
          <Table striped responsive className="mb-0 align-middle">
            <thead>
              <tr>
                <th>{t('contacts_department', { defaultValue: 'Reparto' })}</th>
                <th>{t('contacts_email', { defaultValue: 'Email' })}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {CONTACTS.map((contact) => (
                <tr key={contact.email}>
                  <td className="fw-semibold">{contact.label}</td>
                  <td>
                    <a
                      href={`mailto:${contact.email}`}
                      className="d-inline-flex align-items-center gap-2 text-break"
                    >
                      <FaEnvelope className="flex-shrink-0" />
                      {contact.email}
                    </a>
                  </td>
                  <td className="text-end">
                    <CopyEmailButton email={contact.email} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ContactsPage;
